import { isLatLng, isLatLngBounds, type LatLng, type LatLngBounds } from './types.ts'

export function getBoundsCenter(bounds: LatLngBounds): LatLng {
  return {
    lat: (bounds.north + bounds.south) / 2,
    lng: (bounds.east + bounds.west) / 2,
  }
}

export function resizeBounds(bounds: LatLngBounds, percentage: number): LatLngBounds {
  // Convert percentage to decimal (e.g., 50% -> 0.5)
  const factor = percentage / 100

  // Calculate the current height and width
  const height = bounds.north - bounds.south
  const width = bounds.east - bounds.west

  // Calculate the change in size
  const latDelta = (height * factor) / 2
  const lngDelta = (width * factor) / 2

  return {
    north: bounds.north + latDelta,
    south: bounds.south - latDelta,
    east: bounds.east + lngDelta,
    west: bounds.west - lngDelta,
  }
}

export function getBboxSize(bounds: LatLngBounds): {
  width: number
  height: number
} {
  // Convert degrees to meters (roughly, at 60° latitude)
  const DEGREE_TO_METERS = 55_500

  const height = (bounds.north - bounds.south) * DEGREE_TO_METERS
  const width = (bounds.east - bounds.west) * DEGREE_TO_METERS

  return { width, height }
}

export function positionToBounds(position: LatLng, size = 1000): LatLngBounds {
  // Convert meters to degrees
  // For latitude: 1 degree = 111,111 meters (approximately)
  const latDelta = size / 2 / 111_111

  // For longitude: need to account for the cosine of the latitude
  // as longitude degrees vary with latitude
  const lngDelta = size / 2 / (111_111 * Math.cos((position.lat * Math.PI) / 180))

  return {
    north: position.lat + latDelta,
    south: position.lat - latDelta,
    east: position.lng + lngDelta,
    west: position.lng - lngDelta,
  }
}

export function positionToBoundsRadius(position: LatLng, radius: number): LatLngBounds {
  return positionToBounds(position, radius * 2)
}

export function getBoundsWidth(bounds: LatLngBounds): number {
  const center = getBoundsCenter(bounds)

  // Width in degrees
  const width = bounds.east - bounds.west

  // Convert to meters using the cosine of the center latitude
  // 111111 meters per degree at the equator, adjusted for latitude
  return width * 111_111 * Math.cos((center.lat * Math.PI) / 180)
}

export function calculateBoundsFromGeometries(
  geometries: (LatLngBounds | LatLng)[]
): LatLngBounds | null {
  if (!geometries.length) {
    return null
  }

  let north = -90
  let south = 90
  let east = -180
  let west = 180

  for (const geometry of geometries) {
    if (isLatLngBounds(geometry)) {
      north = Math.max(north, geometry.north)
      south = Math.min(south, geometry.south)
      east = Math.max(east, geometry.east)
      west = Math.min(west, geometry.west)
    } else if (isLatLng(geometry)) {
      north = Math.max(north, geometry.lat)
      south = Math.min(south, geometry.lat)
      east = Math.max(east, geometry.lng)
      west = Math.min(west, geometry.lng)
    }
  }

  return { north, south, east, west }
}

export function calculateCenterFromBounds(bounds: LatLngBounds): LatLng {
  return getBoundsCenter(bounds)
}
